import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from 'idb'

export interface ModelAsset {
  id: string
  name: string
  blob: Blob
  addedAt: number
}

interface GalleryDB extends DBSchema {
  models: { key: string; value: ModelAsset }
}

const DB_NAME = 'xr-gallery'
const STORE = 'models'

let dbPromise: Promise<IDBPDatabase<GalleryDB>> | null = null

function db() {
  if (!dbPromise) {
    dbPromise = openDB<GalleryDB>(DB_NAME, 1, {
      upgrade(d) {
        d.createObjectStore(STORE, { keyPath: 'id' })
      },
    })
  }
  return dbPromise
}

export function isModelFile(name: string): boolean {
  return /\.(glb|gltf)$/i.test(name)
}

const decoder = new TextDecoder()

// Content check, not just extension: a binary glb starts with the "glTF" magic, a gltf is JSON
// ('{'). Rejects HTML masquerading as a model — e.g. the SPA fallback served when model.glb is
// missing, which otherwise gets stored and crashes useGLTF on spawn.
export async function isModelBlob(blob: Blob): Promise<boolean> {
  let head: Uint8Array
  try {
    head = new Uint8Array(await blob.slice(0, 64).arrayBuffer())
  } catch {
    return true // unreadable blob (only happens in the jsdom test env) — never purge blindly
  }
  if (decoder.decode(head.slice(0, 4)) === 'glTF') return true
  return decoder.decode(head).trimStart().startsWith('{')
}

export async function addModel(file: File): Promise<ModelAsset> {
  if (!(await isModelBlob(file))) throw new Error(`${file.name} is not a valid glb/gltf`)
  const asset: ModelAsset = {
    id: crypto.randomUUID(),
    name: file.name,
    blob: file,
    addedAt: Date.now(),
  }
  await (await db()).put(STORE, asset)
  return asset
}

export async function listModels(): Promise<ModelAsset[]> {
  const all = await (await db()).getAll(STORE)
  // Self-heal: purge any entry stored before content validation existed (e.g. HTML-as-glb).
  const valid = await Promise.all(all.map((m) => isModelBlob(m.blob)))
  await Promise.all(all.filter((_, i) => !valid[i]).map((m) => deleteModel(m.id)))
  return all.filter((_, i) => valid[i]).sort((a, b) => b.addedAt - a.addedAt)
}

export async function getModel(id: string): Promise<ModelAsset | undefined> {
  return (await db()).get(STORE, id)
}

export async function deleteModel(id: string): Promise<void> {
  await (await db()).delete(STORE, id)
}

// Nuke the whole database — the lobby's escape hatch when a stored model wedges the app. Closes our
// open connection first so the delete doesn't block; the next db() call reopens a fresh, empty DB.
export async function resetDatabase(): Promise<void> {
  if (dbPromise) {
    try {
      ;(await dbPromise).close()
    } catch {
      // already closing/closed
    }
    dbPromise = null
  }
  await deleteDB(DB_NAME)
}
